/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * "What we think is wrong" — the ranked diagnosis (#895 · R3).
 *
 * Sits below the outcome strip and above the payloads: claim, then remedy, then
 * proof. The order is the point. The #882/#883 tester had complete and honest
 * evidence and still could not close the loop, because nothing in the product
 * ever said what it believed was wrong.
 *
 * Each row leads with its confidence, verbatim from `CONFIDENCE_LABEL`, so a
 * claim can be weighed before it is acted on — "Most likely" is reserved for
 * something visible in the payload that was actually sent, "Worth checking" for
 * a coverage gap, "The exchange said" for their words, attributed to them.
 */

import React from 'react';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import {
  CONFIDENCE_LABEL,
  type DiagnosisCause,
  type DiagnosisConfidence,
  type DiagnosisFix,
} from '../../utils/test-diagnosis';

/**
 * Ink per confidence. Deliberately not a severity scale — `exchange-said` is
 * neutral because it is a quotation, not our judgement of it.
 */
const CONFIDENCE_INK: Record<DiagnosisConfidence, string> = {
  likely: 'error.ink',
  'worth-checking': 'warning.ink',
  'exchange-said': 'fg.default',
  'no-signal': 'fg.muted',
};

interface TestDiagnosisListProps {
  causes: readonly DiagnosisCause[];
  /**
   * Stages an edit on the surface the cause points at. Omitted where no editor
   * is open (the list page) — a Fix that can only navigate away is the nav hop
   * the guidance rules out, so no affordance is shown at all.
   */
  onFix?: (fix: DiagnosisFix) => void;
}

export const TestDiagnosisList: React.FC<TestDiagnosisListProps> = ({ causes, onFix }) => {
  if (causes.length === 0) return null;

  return (
    <Box mt={3}>
      <Text fontSize="xs" fontWeight="700" color="fg.muted" letterSpacing="0.06em" textTransform="uppercase" mb={2}>
        What we think is wrong
      </Text>

      <Flex direction="column" gap={2}>
        {causes.map((cause, i) => (
          <Flex
            key={`${cause.confidence}-${cause.ref ?? i}`}
            align="flex-start"
            gap={3}
            p={2.5}
            borderWidth="1px"
            borderStyle="solid"
            borderColor="border.default"
            borderRadius="6px"
            bg="bg.card"
          >
            <Box flex="1" minW={0}>
              <Text fontSize="xs" color="fg.default" lineHeight="1.5">
                <Text as="span" fontWeight="700" color={CONFIDENCE_INK[cause.confidence]}>
                  {CONFIDENCE_LABEL[cause.confidence]}.
                </Text>{' '}
                {cause.message}
              </Text>
              {cause.ref && (
                <Text fontSize="xs" fontFamily="mono" color="fg.muted" mt={1} wordBreak="break-all">
                  {cause.ref}
                </Text>
              )}
            </Box>

            {cause.fix && onFix && (
              <Button
                size="xs"
                variant="ghost"
                color="accent.solid"
                flexShrink={0}
                onClick={() => onFix(cause.fix as DiagnosisFix)}
              >
                Fix
              </Button>
            )}
          </Flex>
        ))}
      </Flex>
    </Box>
  );
};
